/**
 * Core-content cutover mode (client half).
 *
 * These deployment gates are reversible and all DEFAULT TO THE SAFE POSTURE
 * when unset:
 *
 *   VITE_CORE_CONTENT_READ_ONLY   (default: '1' = read-only ON)
 *   VITE_KEEPSAKES_ENABLED        (default: '0' = keepsakes OFF)
 *   VITE_ENHANCED_VOICE_ENABLED   (default: '0' = cloud audio OFF)
 *
 * A build with no env configuration is therefore a safe build. Setting
 * VITE_CORE_CONTENT_READ_ONLY=0 is the ONLY way to re-enable core writes.
 * Enhanced Voice remains an independent opt-in; typed writes must never turn
 * cloud audio on as a side effect.
 *
 * `assertCoreWriteAllowed` is the SHARED code-level write guard. Every
 * store mutation entry point (prayers, bookmarks, highlights, audio
 * queue, keepsake intents, outbox pushes, enrichment) calls it BEFORE
 * touching IndexedDB or the network. Hiding a button is presentation;
 * this guard is the invariant: a bypassed UI call still rejects before
 * changing any local byte.
 *
 * Server mirror: api/_lib/core-cutover.ts (CORE_CONTENT_READ_ONLY /
 * KEEPSAKES_ENABLED process env vars). Cross-boundary duplication per
 * CLAUDE.md rule #4: src/ and api/ deliberately do not share modules.
 */

import { isOwnerTransitionWriteBarrierActive } from './owner-transition';

// Optional chaining is load-bearing for the probes, NOT decoration.
// Vite always defines `import.meta.env`, but plain Node (which is what
// runs scripts/probe-core-readonly.mjs and the two IDB probes under tsx)
// leaves `import.meta` without an `env` property, so a bare
// `import.meta.env.X` throws on import and takes every probe that loads
// db.ts down with it. Reading through `?.` yields undefined there, which
// lands on exactly the safe defaults below: read-only ON, keepsakes and
// Enhanced Voice OFF.
const RAW_READ_ONLY = import.meta.env?.VITE_CORE_CONTENT_READ_ONLY as unknown;
const RAW_KEEPSAKES = import.meta.env?.VITE_KEEPSAKES_ENABLED as unknown;
const RAW_ENHANCED_VOICE = import.meta.env?.VITE_ENHANCED_VOICE_ENABLED as unknown;

/** True unless the operator explicitly sets VITE_CORE_CONTENT_READ_ONLY=0. */
export const CORE_CONTENT_READ_ONLY: boolean =
  !(typeof RAW_READ_ONLY === 'string' && RAW_READ_ONLY.trim() === '0');

/** False unless the operator explicitly sets VITE_KEEPSAKES_ENABLED=1. */
export const KEEPSAKES_ENABLED: boolean =
  typeof RAW_KEEPSAKES === 'string' && RAW_KEEPSAKES.trim() === '1';

/** False unless the operator explicitly opts cloud-audio capture in. */
export const ENHANCED_VOICE_ENABLED: boolean =
  typeof RAW_ENHANCED_VOICE === 'string' && RAW_ENHANCED_VOICE.trim() === '1';

/** Thrown by the shared write guard when a mutation is attempted in read-only mode. */
export class CoreReadOnlyError extends Error {
  readonly code = 'core-read-only';
  readonly op: string;
  constructor(op: string) {
    super(`core-content read-only: rejected write operation '${op}'`);
    this.name = 'CoreReadOnlyError';
    this.op = op;
  }
}

export function isCoreReadOnly(): boolean {
  return CORE_CONTENT_READ_ONLY;
}

/** Both independent client gates must be open before cloud-audio work. */
export function enhancedVoiceAllowed(): boolean {
  return ENHANCED_VOICE_ENABLED && !CORE_CONTENT_READ_ONLY;
}

/**
 * The shared write guard. `op` is a short machine label ('prayer:add',
 * 'bookmark:update', 'highlight:remove', 'audio:enqueue', ...). It must
 * NEVER carry prayer text, note text, or any user content.
 */
export function assertCoreWriteAllowed(op: string): void {
  if (CORE_CONTENT_READ_ONLY) {
    throw new CoreReadOnlyError(op);
  }
  if (isOwnerTransitionWriteBarrierActive()) {
    throw new Error(`owner transition active: rejected write operation '${op}'`);
  }
}
